/**
 * `external-sharing` Phase 6 Session 6.3 — interactive Google Maps surface.
 *
 * Replaces the Phase-6 `<SatelliteImage>` placeholder at the same mount
 * points (`<PropertyVisualGrid>`'s `satellite` slot). Loads the Maps JS API
 * lazily via `loadMaps()` (singleton) — only mounts the SDK when this
 * component renders, so the ~80KB SDK download is paid once per session
 * the user reaches a Signal/Model tab.
 *
 * 4 states (per the frontend agent's checklist):
 * - **No coordinates**: lat/lng null/undefined → `<EmptyState>`-ish copy.
 *   Don't even attempt to load the SDK.
 * - **Loading**: SDK fetch in flight → `<Skeleton>` block.
 * - **Error**: SDK fetch failed OR API key missing → friendly retry UI.
 * - **Success**: SDK ready → interactive Maps + custom controls + pulsing
 *   marker.
 *
 * Behaviors:
 * - `disableDefaultUI: true` — Google's controls are hidden; we render
 *   `<PropertyMapControls>` overlay.
 * - `gestureHandling: 'cooperative'` — requires Cmd/Ctrl + scroll to zoom,
 *   preventing accidental zoom-while-page-scrolling.
 * - `mapId` from `VITE_GOOGLE_MAPS_MAP_ID` for Cloud-managed theming
 *   (POI labels hidden, satellite-mode style).
 * - Marker uses `AdvancedMarkerElement` (NOT the deprecated `Marker` class)
 *   with custom HTML `content` from `createPropertyMarkerElement()`.
 *
 * Privacy: API key is referrer-restricted server-side (GCP Console
 * config). Bundle leak is acceptable because requests from non-allowlisted
 * referrers fail at the API gateway. SDK fetches tiles directly from
 * Google CDN — no proxy through our API.
 *
 * **DO NOT** use the legacy `Marker` class — Google deprecated it 2024.
 * **DO NOT** load the SDK eagerly — bundle-size impact is real.
 */
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { GoogleMapsConfigError, loadMaps } from '@/lib/google-maps-loader';
import { PropertyMapControls } from './PropertyMapControls';
import { createPropertyMarkerElement } from './PropertyMapMarker';

export interface PropertyMapProps {
  /** Latitude — when null/undefined, no-coordinates state renders. */
  lat: number | null | undefined;
  /** Longitude — same nullability semantics as lat. */
  lng: number | null | undefined;
  /** Initial zoom level. Defaults to 17 (~150m wide footprint). */
  zoom?: number;
  className?: string;
}

const DEFAULT_ZOOM = 17;
const MIN_ZOOM = 14;
const MAX_ZOOM = 20;

type LoadState =
  | { kind: 'no-coords' }
  | { kind: 'loading' }
  | { kind: 'error'; error: Error; isConfigError: boolean }
  | { kind: 'ready' };

export function PropertyMap({
  lat,
  lng,
  zoom = DEFAULT_ZOOM,
  className,
}: PropertyMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(
    null
  );
  const [retryToken, setRetryToken] = useState(0);

  const hasCoords =
    typeof lat === 'number' &&
    Number.isFinite(lat) &&
    typeof lng === 'number' &&
    Number.isFinite(lng);

  const [state, setState] = useState<LoadState>(() =>
    hasCoords ? { kind: 'loading' } : { kind: 'no-coords' }
  );

  // Sync state when coords become available (or disappear).
  useEffect(() => {
    if (!hasCoords) {
      setState({ kind: 'no-coords' });
      return;
    }
    setState((prev) =>
      prev.kind === 'no-coords' ? { kind: 'loading' } : prev
    );
  }, [hasCoords]);

  // Load the SDK + initialize the map. Re-runs on retry.
  useEffect(() => {
    if (!hasCoords) return undefined;
    if (state.kind !== 'loading') return undefined;

    let cancelled = false;
    (async () => {
      try {
        const runtime = await loadMaps();
        if (cancelled || !containerRef.current) return;
        const center = { lat: lat as number, lng: lng as number };
        const map = new runtime.Map(containerRef.current, {
          center,
          zoom,
          mapId: runtime.mapId,
          disableDefaultUI: true,
          gestureHandling: 'cooperative',
          clickableIcons: false,
          mapTypeId: 'satellite',
        });
        const marker = new runtime.AdvancedMarkerElement({
          map,
          position: center,
          content: createPropertyMarkerElement(),
        });
        mapRef.current = map;
        markerRef.current = marker;
        setState({ kind: 'ready' });
      } catch (err) {
        if (cancelled) return;
        const error = err instanceof Error ? err : new Error(String(err));
        setState({
          kind: 'error',
          error,
          isConfigError: error instanceof GoogleMapsConfigError,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hasCoords, lat, lng, zoom, state.kind, retryToken]);

  // Recenter when coords change after the map is initialized.
  useEffect(() => {
    if (state.kind !== 'ready') return;
    if (!hasCoords) return;
    const center = { lat: lat as number, lng: lng as number };
    mapRef.current?.setCenter(center);
    if (markerRef.current) {
      markerRef.current.position = center;
    }
  }, [state.kind, hasCoords, lat, lng]);

  // Recenter on container resize. Google Maps anchors the top-left geographic
  // point (NOT the center) when its container resizes, so the marker drifts
  // off-center after any size change — tab switch, fullscreen toggle, window
  // resize, or the first reveal from a zero-size mount. Re-applying setCenter on
  // every ResizeObserver tick keeps the pin centered. The observer also fires
  // once on observe(), which harmlessly re-centers right after the map mounts.
  useEffect(() => {
    if (state.kind !== 'ready') return undefined;
    if (!hasCoords) return undefined;
    const container = containerRef.current;
    const map = mapRef.current;
    if (!container || !map || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const center = { lat: lat as number, lng: lng as number };
    const observer = new ResizeObserver(() => {
      map.setCenter(center);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [state.kind, hasCoords, lat, lng]);

  // Cleanup: detach the marker on unmount.
  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
      mapRef.current = null;
    };
  }, []);

  function handleZoomIn() {
    const map = mapRef.current;
    if (!map) return;
    const next = Math.min((map.getZoom() ?? zoom) + 1, MAX_ZOOM);
    map.setZoom(next);
  }
  function handleZoomOut() {
    const map = mapRef.current;
    if (!map) return;
    const next = Math.max((map.getZoom() ?? zoom) - 1, MIN_ZOOM);
    map.setZoom(next);
  }
  function handleRecenter() {
    const map = mapRef.current;
    if (!map || !hasCoords) return;
    map.setCenter({ lat: lat as number, lng: lng as number });
    map.setZoom(zoom);
  }

  // No-coords state — same UI shape as the prior `<SatelliteImage>` empty
  // state so PropertyVisualGrid stays visually stable.
  if (state.kind === 'no-coords') {
    return (
      <div
        data-testid="property-map"
        data-state="empty"
        className={clsx(
          'h-full w-full flex items-center justify-center',
          'border border-panel-border rounded-[3px] bg-panel-bg',
          'text-micro font-mono text-text-dim text-center px-3',
          className
        )}
      >
        No coordinates
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div
        data-testid="property-map"
        data-state="error"
        className={clsx(
          'h-full w-full flex flex-col items-center justify-center gap-2',
          'border border-panel-border rounded-[3px] bg-panel-bg p-4 text-center',
          className
        )}
      >
        <span className="font-mono text-micro text-text-bright uppercase tracking-[0.5px]">
          {state.isConfigError ? 'MAP NOT CONFIGURED' : 'MAP UNAVAILABLE'}
        </span>
        <span className="text-xs text-text-muted">
          {state.isConfigError
            ? "The map provider isn't set up for this environment."
            : "Couldn't load the satellite map."}
        </span>
        {!state.isConfigError && (
          <Button
            variant="secondary"
            onClick={() => {
              setRetryToken((n) => n + 1);
              setState({ kind: 'loading' });
            }}
            data-testid="property-map-retry"
            className="!py-1 !px-2 mt-1"
          >
            Retry
          </Button>
        )}
      </div>
    );
  }

  // Loading + ready states share the same DOM container; the map renders
  // into `containerRef` once `state.kind === 'ready'`. We keep the
  // container mounted so the SDK's child nodes have a stable host.
  return (
    <div
      data-testid="property-map"
      data-state={state.kind}
      data-lat={lat as number}
      data-lng={lng as number}
      className={clsx(
        'relative h-full w-full overflow-hidden',
        'border border-panel-border rounded-[3px] bg-panel-bg',
        className
      )}
    >
      <div ref={containerRef} className="absolute inset-0" />
      {state.kind === 'loading' && (
        <div
          data-testid="property-map-loading"
          className="absolute inset-0 flex items-center justify-center"
        >
          <Skeleton variant="block" className="w-full h-full" />
        </div>
      )}
      {state.kind === 'ready' && (
        <PropertyMapControls
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onRecenter={handleRecenter}
        />
      )}
    </div>
  );
}
